import React, { useEffect, useRef, useState } from 'react'
import api from '../../api/axios';
import { ChevronDown, DownloadIcon } from 'lucide-react';
import Popup from '../popup/Popup';
import QRDesignerComponent from '../../modules/qr-editor/QREditor';

const Layout5Form = ({ data, onChange, errors }) => {
    const { video, brand, title, products, infoText, discountText } = data;

    const [campaigns, setCampaigns] = useState([]);
    const [openQrCodePopup, setOpenQrCodePopup] = useState(false);
    const fieldRefs = useRef({});

    useEffect(() => {
        if (!errors) return;

        const firstErrorKey = Object.keys(errors)[0];
        const el = fieldRefs.current[firstErrorKey];

        if (el) {
            el.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [errors]);

    const handleChange = (section, field, value) => {
        let cleanedValue = value;

        if (typeof value === "string") {
            // cleanedValue = (value || '').replace(/%/g, "");
        }

        if (field === 'url' || field === 'url2' || field === 'logoUrl' || field === 'imageUrl' || field === 'qrCodeUrl') {
            cleanedValue = value;
        }
        onChange('layout5', section, field, cleanedValue);
    };

    const getCampaigns = async () => {
        try {
            const response = await api.get('campaign/list');
            if (response && response.data && response.data.data) {
                setCampaigns(response.data.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getCampaigns();
    }, []);

    return (
        <div className="h-[calc(100vh-220px)]">
            {/* Canvas Settings */}
            <div className="form-section">
                <div className="form-group">
                    <label>Campaign Name</label>
                    <div className='relative'>
                        <select
                            ref={(el) => (fieldRefs.current["camp_name"] = el)}
                            value={data.camp_name}
                            onChange={(e) => handleChange(null, 'camp_name', e.target.value)}
                            className={`appearance-none ${errors?.camp_name ? "has-error" : ""} `}
                        >
                            <option value="">Select Campaign</option>
                            {campaigns.map((camp) => (
                                <option key={camp.camp_id} value={camp.camp_name}>
                                    {camp.camp_name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
                    </div>
                    {errors?.camp_name && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.camp_name}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Brand Name</label>
                    <input
                        type="text"
                        ref={(el) => (fieldRefs.current["brand_name"] = el)}
                        value={data.brand_name || ''}
                        onChange={(e) => handleChange(null, 'brand_name', e.target.value)}
                        className={errors?.brand_name ? "has-error" : ""}
                    />
                    {errors?.brand_name && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.brand_name}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Domain</label>
                    <input
                        type="text"
                        ref={(el) => (fieldRefs.current["domain"] = el)}
                        value={data.domain || ''}
                        onChange={(e) => handleChange(null, 'domain', e.target.value)}
                        className={errors?.domain ? "has-error" : ""}
                    />
                    {errors?.domain && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.domain}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Creative Name</label>
                    <input
                        type="text"
                        ref={(el) => (fieldRefs.current["creative_name"] = el)}
                        value={data.creative_name || ''}
                        onChange={(e) => handleChange(null, 'creative_name', e.target.value)}
                        className={errors?.creative_name ? "has-error" : ""}
                    />
                    {errors?.creative_name && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.creative_name}
                        </p>
                    )}
                </div>
            </div>

            {/* Video Section */}
            <div className="form-section">
                <h3>Canvas and video settings</h3>
                <>
                    <div className="form-group">
                        <label>Gradient Color 1</label>
                        <div className="color-input-group">
                            <input
                                type="color"
                                value={data.canvas?.gradientColor1 || '#6b1f23'}
                                onChange={(e) => handleChange('canvas', 'gradientColor1', e.target.value)}
                            />
                            <input
                                type="text"
                                value={data.canvas?.gradientColor1 || '#6b1f23'}
                                onChange={(e) => handleChange('canvas', 'gradientColor1', e.target.value)}
                                placeholder="#6b1f23"
                                className="color-text-input"
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Gradient Color 2</label>
                        <div className="color-input-group">
                            <input
                                type="color"
                                value={data.canvas?.gradientColor2 || '#a24a43'}
                                onChange={(e) => handleChange('canvas', 'gradientColor2', e.target.value)}
                            />
                            <input
                                type="text"
                                value={data.canvas?.gradientColor2 || '#a24a43'}
                                onChange={(e) => handleChange('canvas', 'gradientColor2', e.target.value)}
                                placeholder="#a24a43"
                                className="color-text-input"
                            />
                        </div>
                    </div>
                    <div className="form-group hidden">
                        <label>Gradient Angle (deg)</label>
                        <input
                            type="number"
                            min="0"
                            max="360"
                            value={data.canvas?.gradientAngle ?? 135}
                            onChange={(e) => handleChange('canvas', 'gradientAngle', parseInt(e.target.value, 10) || 0)}
                        />
                    </div>
                </>

                <div className="form-group">
                    <label>Video URL (1920 x 1080)</label>
                    <input
                        type="text"
                        value={video.url}
                        onChange={(e) => handleChange('video', 'url', e.target.value)}
                        placeholder="Enter video URL"
                        ref={(el) => (fieldRefs.current["video.url"] = el)}
                    />
                    <span className='text-xs text-blue-500'>Aspect ratio: (1920 x 1080)</span>
                    {errors?.['video.url'] && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors['video.url']}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>GIF or Video URL</label>
                    <input
                        type="text"
                        value={video?.url2 || ''}
                        onChange={(e) => handleChange('video', 'url2', e.target.value)}
                        placeholder="Enter GIF or Video URL"
                        ref={(el) => (fieldRefs.current["video.url2"] = el)}
                    />
                    <span className='text-xs text-blue-500'>Aspect ratio: 9:16 (vertical)</span>
                    {errors?.['video.url2'] && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors['video.url2']}
                        </p>
                    )}
                </div>

                {/* Title – updated maxLength to 41 */}
                <div className="form-group">
                    <label>Title</label>
                    <input
                        type="text"
                        value={title || ''}
                        onChange={(e) => handleChange(null, 'title', e.target.value)}
                        placeholder="Give your wardrobe a spring refresh"
                        maxLength={41}
                        minLength={3}
                        ref={(el) => (fieldRefs.current["title"] = el)}
                        required
                    />
                    <p className="text-xs text-blue-500 mt-1">Min: 3 characters, Max: 41 characters</p>
                    {errors?.title && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.title}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Text Color</label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            value={data.canvas?.textColor || '#121212'}
                            onChange={(e) => handleChange('canvas', 'textColor', e.target.value)}
                        />
                        <input
                            type="text"
                            value={data.canvas?.textColor || '#121212'}
                            onChange={(e) => handleChange('canvas', 'textColor', e.target.value)}
                            placeholder="#121212"
                            className="color-text-input"
                        />
                    </div>
                </div>

                {/* Discount Text – updated min/max */}
                <div className="form-group">
                    <label>Discount Text</label>
                    <input
                        type="text"
                        value={discountText || ''}
                        onChange={(e) => handleChange(null, 'discountText', e.target.value)}
                        placeholder="Get 20% Off"
                        maxLength={16}
                        minLength={3}
                        ref={(el) => (fieldRefs.current["discountText"] = el)}
                        required
                    />
                    <p className="text-xs text-blue-500 mt-1">Min: 3 characters, Max: 16 characters</p>
                    {errors?.discountText && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.discountText}
                        </p>
                    )}
                </div>

                {/* Info Text – updated min/max */}
                <div className="form-group">
                    <label>Info Text</label>
                    <input
                        type="text"
                        value={infoText || ''}
                        onChange={(e) => handleChange(null, 'infoText', e.target.value)}
                        placeholder="all our models"
                        maxLength={21}
                        minLength={3}
                        ref={(el) => (fieldRefs.current["infoText"] = el)}
                        required
                    />
                    <p className="text-xs text-blue-500 mt-1">Min: 3 characters, Max: 21 characters</p>
                    {errors?.infoText && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.infoText}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Discount Text Color</label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            value={data.canvas?.discountTextColor || '#121212'}
                            onChange={(e) => handleChange('canvas', 'discountTextColor', e.target.value)}
                        />
                        <input
                            type="text"
                            value={data.canvas?.discountTextColor || '#121212'}
                            onChange={(e) => handleChange('canvas', 'discountTextColor', e.target.value)}
                            placeholder="#121212"
                            className="color-text-input"
                        />
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3>Products</h3>

                {[0, 1].map((index) => (
                    <div key={index} className="form-group">
                        <label>Product {index + 1} URL</label>
                        <input
                            type="text"
                            value={products[index] || ""}
                            placeholder="Enter product image URL"
                            onChange={(e) => {
                                const updatedProducts = [...products];
                                updatedProducts[index] = e.target.value;
                                handleChange(null, "products", updatedProducts);
                            }}
                            ref={(el) => (fieldRefs.current[`products[${index}]`] = el)}
                        />
                        {/* Updated product image info */}
                        <p className="text-xs text-blue-500 mt-1">Min: 400×400 pixels (square), no maximum limit</p>
                        {errors?.[`products[${index}]`] && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors[`products[${index}]`]}
                            </p>
                        )}
                    </div>
                ))}
            </div>

            {/* Brand Section */}
            <div className="form-section">
                <h3>Brand</h3>

                {/* Logo – updated dimensions */}
                <div className="form-group">
                    <label>Logo URL</label>
                    <input
                        type="text"
                        value={brand.logoUrl || ''}
                        onChange={(e) => handleChange('brand', 'logoUrl', e.target.value)}
                        placeholder="Enter logo URL"
                        ref={(el) => (fieldRefs.current["brand.logoUrl"] = el)}
                    />
                    <p className="text-xs text-blue-500 mt-1">Min: 300×100, Max: 800×100 pixels</p>
                    {errors?.['brand.logoUrl'] && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors['brand.logoUrl']}
                        </p>
                    )}
                </div>

                {/* QR Code – updated min */}
                <div className="form-group">
                    <label className='!flex justify-between cursor-pointer'
                        onClick={() => setOpenQrCodePopup(true)}>QR Code URL <span className='hover:text-blue-800 mr-2'
                        ><DownloadIcon className='h-4 w-4' /></span>
                    </label>
                    <input
                        type="text"
                        value={brand?.qrCodeUrl || ''}
                        onChange={(e) => handleChange('brand', 'qrCodeUrl', e.target.value)}
                        placeholder="Enter QR code URL"
                        ref={(el) => (fieldRefs.current["brand.qrCodeUrl"] = el)}
                    />
                    <p className="text-xs text-blue-500 mt-1">Min: 300×300 pixels (square), no maximum limit</p>
                    {errors?.['brand.qrCodeUrl'] && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors['brand.qrCodeUrl']}
                        </p>
                    )}
                </div>
            </div>
            <Popup isOpen={openQrCodePopup} onClose={() => setOpenQrCodePopup(false)}>
                <QRDesignerComponent />
            </Popup>
        </div>
    )
}

export default Layout5Form